import { BehaviorActionType } from '@/common/model/recommend/behavior'
import { WidgetDisplayLogic } from '@/common/model/recommend/widget'

export const CustomerApiPath = {
  LIST_CUSTOMERS: '/api/sites/{site_id}/customers', // GET List Customers
  BEHAVIOR_ANALYSIS: '/api/sites/{site_id}/customers/{visitor_id}/behavior-analysis', // GET Behavior Analysis
  GENERATE_CUSTOMER_INSIGHT: '/api/sites/{site_id}/customers/{visitor_id}/insight', // POST Generate Customer Insight
  // 2026-10-04 追加（SMART_HUB_DEV-127）：訪問者サマリとそのAI示唆
  VISITOR_SUMMARY: '/api/sites/{site_id}/customers/summary', // GET Visitor Summary
  VISITOR_SUMMARY_INSIGHTS: '/api/sites/{site_id}/customers/summary/insights' // GET List / POST Run
}

export type CustomerApiPath = (typeof CustomerApiPath)[keyof typeof CustomerApiPath]

export interface ListCustomersVars {
  site_id: number
  page?: number
  per_page?: number
  status?: string | null
  search?: string | null
  sort?:
    | 'visitor_id'
    | 'customer_id'
    | 'delivery_count'
    | 'view_count'
    | 'view_product_count'
    | 'cart_count'
    | 'order_count'
    | 'first_seen'
    | 'last_seen'
    | null
  order?: 'asc' | 'desc' | null
}

export const BehaviorTimelineActionType = {
  ...BehaviorActionType,
  // for behavior analysis
  PURCHASE: 'purchase',
  RECOMMENDATION_CLICK: 'recommendation_click',
  RECOMMENDATION_DELIVERY: 'recommendation_delivery'
}

export type BehaviorTimelineActionType =
  (typeof BehaviorTimelineActionType)[keyof typeof BehaviorTimelineActionType]

export interface BehaviorTimeline {
  type: 'behavior' | 'order' | 'delivery' | 'rec_click' | 'other'
  action: BehaviorTimelineActionType
  timestamp: string | null
  session_id?: string
  device_type?: string
  browser?: string
  page_url?: string
  item_id?: string | null
  product_image?: string | null
  product_name?: string | null
  // widget
  display_logic?: WidgetDisplayLogic
  widget_id?: number
  spec_id?: string | null
  //
  unit_price?: number
  quantity?: number
  order_number?: string
}

export interface BehaviorAnalysisVars {
  site_id: number
  visitor_id: string
}

export interface GenerateCustomerInsightVars {
  site_id: number
  visitor_id: string
  timeline: BehaviorTimeline[]
  summary: Record<string, unknown>
}

export interface CustomerSummary {
  visitor_id: string
  customer_id: string | null
  device_type: string | null
  order_count: number
  view_count: number
  view_product_count: number
  cart_count: number
  delivery_count: number
  first_seen: string | null
  last_seen: string | null
}

export interface ListCustomersResponse {
  customers: CustomerSummary[]
  total: number
}

export interface BehaviorAnalysisResponse {
  visitor_id: string
  customer_id: string | null
  timeline: BehaviorTimeline[]
  views_before_first_purchase: number
  device_counts: Record<string, number>
  browser_counts: Record<string, number>
  purchase_device_counts: Record<string, number>
  total_orders: number
  total_behavior_events: number
  total_deliveries: number
  total_rec_clicks: number
  ai_tags_interest: { category: string; count: number; tag_name: string }[]
}

export interface GenerateCustomerInsightResponse {
  insight: string
}

// ------------------------------------------------------------------
// 訪問者サマリ（2026-10-04 追加・SMART_HUB_DEV-127）
// 会員／ゲスト・配信接触の到達率比較はわざと持たない（因果として読めないため）
// ------------------------------------------------------------------

export interface VisitorSummaryVars {
  site_id: number
  /** 30 または 90 */
  days: number
}

/** 訪問者単位の到達人数と率。率は 0〜1。分母が 0 のときは null */
export interface VisitorFunnel {
  visitors: number
  product_view: number
  cart: number
  /** 注文に到達した訪問者（注文件数ではない） */
  order: number
  cart_after_view: number
  order_after_cart: number
  product_view_rate: number | null
  cart_rate: number | null
  order_rate: number | null
  /** 商品閲覧者のうちカートにも進んだ割合 */
  cart_per_product_view: number | null
  /** カート投入者のうち注文にも到達した割合 */
  order_per_cart: number | null
}

export interface VisitorSegment extends VisitorFunnel {
  /** 流入分類またはデバイス。値が無い訪問者は unknown */
  key: string
  share: number | null
  /** 訪問者が min_segment 未満の参考値 */
  small: boolean
}

export interface VisitorSummaryResponse {
  days: number
  since: string
  generated_at: string
  min_segment: number
  population: { visitors: number; members: number; guests: number; member_share: number | null }
  funnel: VisitorFunnel
  /** 初回の入口の流入分類別 */
  by_referrer: VisitorSegment[]
  /** 最終接触時のデバイス別 */
  by_device: VisitorSegment[]
  counters_synced_at: string | null
}

export interface VisitorInsightFinding {
  question: 'funnel' | 'segment' | 'caution' | string | null
  title: string
  body: string
  segments: string[]
}

export interface VisitorInsightRow {
  id: number
  generation: number
  days: number
  output: { summary: string; findings: VisitorInsightFinding[]; not_covered: string[] }
  warnings: { kind: string; message: string }[]
  model: string | null
  tokens_in: number | null
  tokens_out: number | null
  operator_name: string | null
  created_at: string | null
}

export interface VisitorInsightListVars {
  site_id: number
}

export interface VisitorInsightListResponse {
  items: VisitorInsightRow[]
  keep_generations: number
}

export interface RunVisitorInsightVars {
  site_id: number
  days: number
}

export interface VisitorInsightRunResponse {
  ok: boolean
  error: string | null
  insight: VisitorInsightRow | null
}
